import { useState, useCallback } from "react";
import { useApi } from "./useApi";

/**
 * Hook customizado para validar cupons de desconto
 * Encapsula a lógica de validação via API e gerenciamento de estado
 */
export const useValidateCoupon = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [couponData, setCouponData] = useState(null);
  const api = useApi();

  /**
   * Valida um cupom via API
   * @param {string} code - Código do cupom
   * @returns {Object} Dados do cupom validado { valid, discount_type, discount_value, ... }
   */
  const validateCoupon = useCallback(
    async (code) => {
      if (!code || code.trim() === "") {
        setError("Por favor, informe um código de cupom.");
        return null;
      }

      setLoading(true);
      setError(null);
      setCouponData(null);

      try {
        const response = await api.get(`/coupons/${code.trim()}/validate`);

        // A API retorna: { data: [...], status: 200 }
        // O cupom está em response.data[0]
        const coupon = response.data?.[0];

        if (coupon && coupon.is_valid && !coupon.is_expired) {
          // Normaliza os dados do cupom para o formato esperado pelo componente
          const normalizedCoupon = {
            valid: true,
            code: coupon.code,
            description: coupon.description,
            discount_type: "percentage", // Por enquanto apenas percentual
            discount_value: Number(coupon.discount_percentage || 0),
            expiration_date: coupon.expiration_date,
            // Mantém dados originais também
            ...coupon,
          };

          setCouponData(normalizedCoupon);
          return normalizedCoupon;
        } else {
          const errorMsg = coupon?.is_expired
            ? "Este cupom está expirado."
            : "Cupom inválido ou expirado.";
          setError(errorMsg);
          return null;
        }
      } catch (err) {
        console.error("Erro ao validar cupom:", err);
        const errorMessage =
          err.data?.message ||
          err.message ||
          "Erro ao validar cupom. Tente novamente.";
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  /**
   * Limpa o cupom aplicado e reseta o estado
   */
  const clearCoupon = useCallback(() => {
    setCouponData(null);
    setError(null);
  }, []);

  /**
   * Calcula o valor do desconto baseado no cupom
   * @param {number} totalValue - Valor total do pedido
   * @returns {number} Valor do desconto
   */
  const calculateDiscount = useCallback(
    (totalValue) => {
      if (!couponData || !couponData.valid) return 0;

      const { discount_type, discount_value } = couponData;

      if (discount_type === "percentage") {
        return (totalValue * discount_value) / 100;
      } else if (discount_type === "fixed") {
        return Math.min(discount_value, totalValue);
      }

      return 0;
    },
    [couponData]
  );

  return {
    loading,
    error,
    couponData,
    validateCoupon,
    clearCoupon,
    calculateDiscount,
  };
};
